import { AiState, Traffic, aiInput, newAi } from './ai';
import { CarPerf, CarState, Input, NO_INPUT, carPerf, speedOf, stepCar } from './car';
import type { DriverSpec } from './drivers';
import { dcos, dsin, hashNumbers } from './fmath';
import { mulberry32 } from './rng';
import { LapEvent, LapState, freshLap, gridCar, respawnCar, updateLap, updateStreak } from './laps';
import type { TeamSpec } from './teams';
import type { Track } from './track';
import { CRASH_RESPAWN_TICKS, SLIP_BOOST, SLIP_LATERAL, SLIP_RANGE, TICK_HZ, pxsFromMph } from './tuning';

/**
 * A multi-car race session (design.md A3, B6, B7, M1): qualifying (one car,
 * one lap) and races (six cars, N laps) both run here. Deterministic: the
 * human's inputs are the only outside influence.
 */

export const POINTS = [10, 6, 4, 3, 2, 1];

/** Relative speed into each other that turns a bump into a crash. */
export const CAR_CRASH_MPH = 55;
const CAR_CRASH_PXS = pxsFromMph(CAR_CRASH_MPH);
/** Each car is two circles (front and rear) for car-to-car contact. */
const BODY_OFFSET = 5;
const BODY_RADIUS = 5.5;
const CAR_RESTITUTION = 0.3;
/** Extra wait before rejoining after a crash in "respawn" races. */
const RESPAWN_PENALTY_TICKS = 2 * TICK_HZ;
const GHOST_TICKS = Math.round(1.5 * TICK_HZ);
const STUCK_RESPAWN_TICKS = 5 * TICK_HZ;
/** After the winner finishes, others get this long to take the flag. */
const FLAG_TIMEOUT_TICKS = 60 * TICK_HZ;

export type CrashRule = 'classic' | 'respawn';

export interface RaceOptions {
  laps: number;
  crashRule: CrashRule;
  /** AI field pace (difficulty). */
  aiPace: number;
  seed: number;
}

export interface RacerSpec {
  id: string;
  name: string;
  tag: string;
  team: TeamSpec;
  /** Undefined = human-controlled. */
  driver?: DriverSpec;
}

export type RacerStatus = 'running' | 'finished' | 'out';

export interface Racer {
  spec: RacerSpec;
  index: number;
  car: CarState;
  perf: CarPerf;
  lap: LapState;
  ai: AiState;
  status: RacerStatus;
  gridSlot: number;
  finishTick: number;
  /** Race distance at the moment a car went out (for classifying DNFs). */
  outProgress: number;
  crashes: number;
  ghostTicks: number;
  lowSpeedTicks: number;
  /** In another car's slipstream this tick. */
  inTow: boolean;
}

export type RaceEvent =
  | (LapEvent & { racer: number })
  | { type: 'crash'; racer: number }
  | { type: 'out'; racer: number }
  | { type: 'respawn'; racer: number }
  | { type: 'finish'; racer: number; position: number }
  | { type: 'streak'; racer: number; bonusMph: number }
  | { type: 'contact'; a: number; b: number; impact: number }
  | { type: 'raceOver' };

export interface ResultRow {
  racer: number;
  position: number;
  status: RacerStatus;
  laps: number;
  /** Race time in ticks for finishers. */
  timeTicks: number | null;
  bestLapTicks: number | null;
  points: number;
}

export class RaceWorld {
  readonly track: Track;
  readonly opts: RaceOptions;
  readonly racers: Racer[] = [];
  tick = 0;
  events: RaceEvent[] = [];
  leaderFinishedTick = -1;
  finishOrder: number[] = [];
  over = false;

  /** `gridOrder[slot]` = index into `specs` of the car in that slot. */
  constructor(track: Track, specs: RacerSpec[], gridOrder: number[], opts: RaceOptions) {
    this.track = track;
    this.opts = opts;
    specs.forEach((spec, i) => {
      const slot = gridOrder.indexOf(i);
      this.racers.push({
        spec,
        index: i,
        car: gridCar(track, slot < 0 ? i : slot),
        perf: carPerf(spec.team),
        lap: freshLap(),
        ai: newAi((opts.seed ^ Math.imul(i + 1, 0x9e3779b1)) >>> 0),
        status: 'running',
        gridSlot: slot,
        finishTick: -1,
        outProgress: 0,
        crashes: 0,
        ghostTicks: 0,
        lowSpeedTicks: 0,
        inTow: false,
      });
    });
  }

  /** Inputs for human racers by racer index; AI racers drive themselves. */
  step(human: ReadonlyMap<number, Input> = new Map()): void {
    this.events.length = 0;
    if (this.over) {
      this.tick++;
      return;
    }
    const traffic: Traffic[] = this.racers.map((r) => ({ car: r.car, solid: this.isSolid(r) }));

    // 1. Decide every input from the same snapshot (order-independent).
    const inputs: Input[] = this.racers.map((r) => {
      if (r.status === 'out' || r.car.crashed) return NO_INPUT;
      const cruise = r.status === 'finished';
      const h = human.get(r.index);
      if (h && !cruise) return h;
      return aiInput(r.ai, r.spec.driver ?? COOLDOWN_DRIVER, {
        car: r.car,
        perf: r.perf,
        track: this.track,
        lapsDone: r.lap.lapsDone,
        basePace: this.opts.aiPace,
        traffic,
        cruise,
      });
    });

    // 2. Slipstream: who is tucked in behind someone?
    this.updateTow();

    // 3. Move every car (walls, laps, streak).
    for (const r of this.racers) {
      if (r.status === 'out') continue;
      const car = r.car;
      if (r.ghostTicks > 0) r.ghostTicks--;
      if (car.crashed) {
        stepCar(car, r.perf, NO_INPUT, this.track);
        if (this.opts.crashRule === 'respawn' && car.crashTicks >= CRASH_RESPAWN_TICKS + RESPAWN_PENALTY_TICKS) {
          this.respawn(r);
        }
        continue;
      }
      const x0 = car.x;
      const y0 = car.y;
      const perf = r.inTow ? { vmax: r.perf.vmax * (1 + SLIP_BOOST), accel: r.perf.accel } : r.perf;
      const res = stepCar(car, perf, inputs[r.index], this.track);
      if (res.crashed) this.onCrash(r);
      if ((r.status as RacerStatus) === 'out') continue;

      const lapEvents: LapEvent[] = [];
      updateLap(this.track, r.lap, this.tick, x0, y0, car.x, car.y, lapEvents);
      for (const e of lapEvents) {
        this.events.push({ ...e, racer: r.index });
        if (e.type === 'lap' && r.status === 'running') this.onLapDone(r);
      }
      if (r.status === 'running' && updateStreak(this.track, car, r.lap, inputs[r.index])) {
        r.perf = carPerf(r.spec.team, car.bonusMph);
        this.events.push({ type: 'streak', racer: r.index, bonusMph: car.bonusMph });
      }

      // Failsafe: anything stuck for 5 s is put back on track.
      if (r.status === 'running' && speedOf(car) < 10) {
        if (++r.lowSpeedTicks > STUCK_RESPAWN_TICKS) this.respawn(r);
      } else {
        r.lowSpeedTicks = 0;
      }
    }

    // 4. Car-to-car contact.
    this.collideCars();

    // 5. Chequered flag / race over.
    const allDone = this.racers.every((r) => r.status !== 'running');
    const timedOut = this.leaderFinishedTick >= 0 && this.tick - this.leaderFinishedTick > FLAG_TIMEOUT_TICKS;
    if (allDone || timedOut) {
      for (const r of this.racers) if (r.status === 'running') this.setOut(r);
      this.over = true;
      this.events.push({ type: 'raceOver' });
    }
    this.tick++;
  }

  private updateTow(): void {
    for (const r of this.racers) {
      r.inTow = false;
      if (!this.isSolid(r)) continue;
      const c = r.car;
      const sp = Math.sqrt(c.vx * c.vx + c.vy * c.vy);
      if (sp < 120) continue;
      const fx = c.vx / sp;
      const fy = c.vy / sp;
      for (const o of this.racers) {
        if (o === r || !this.isSolid(o)) continue;
        const dx = o.car.x - c.x;
        const dy = o.car.y - c.y;
        const along = dx * fx + dy * fy;
        const lat = dx * -fy + dy * fx;
        if (along > 12 && along < SLIP_RANGE && lat < SLIP_LATERAL && lat > -SLIP_LATERAL) {
          r.inTow = true;
          break;
        }
      }
    }
  }

  private isSolid(r: Racer): boolean {
    return r.status === 'running' && !r.car.crashed && r.ghostTicks === 0;
  }

  private onCrash(r: Racer): void {
    r.crashes++;
    r.lap.valid = false;
    r.lap.streakDist = 0;
    this.events.push({ type: 'crash', racer: r.index });
    if (this.opts.crashRule === 'classic' && r.status === 'running') this.setOut(r);
  }

  private setOut(r: Racer): void {
    r.outProgress = this.progress(r);
    r.status = 'out';
    this.events.push({ type: 'out', racer: r.index });
  }

  private onLapDone(r: Racer): void {
    if (r.lap.lapsDone >= this.opts.laps || this.leaderFinishedTick >= 0) {
      r.status = 'finished';
      r.finishTick = this.tick + 1;
      this.finishOrder.push(r.index);
      if (this.leaderFinishedTick < 0) this.leaderFinishedTick = this.tick;
      this.events.push({ type: 'finish', racer: r.index, position: this.finishOrder.length });
    }
  }

  private respawn(r: Racer): void {
    const bonus = r.car.bonusMph;
    r.car = respawnCar(this.track, r.lap.lastGate, bonus);
    r.ghostTicks = GHOST_TICKS;
    r.lowSpeedTicks = 0;
    r.ai.reverseTicks = 0;
    r.ai.stuckTicks = 0;
    this.events.push({ type: 'respawn', racer: r.index });
  }

  private collideCars(): void {
    const rs = this.racers;
    for (let i = 0; i < rs.length; i++) {
      const a = rs[i];
      if (!this.isSolid(a)) continue;
      for (let j = i + 1; j < rs.length; j++) {
        const b = rs[j];
        if (!this.isSolid(b)) continue;
        const ca = a.car;
        const cb = b.car;
        const dxc = cb.x - ca.x;
        const dyc = cb.y - ca.y;
        if (dxc * dxc + dyc * dyc > 30 * 30) continue;
        // Deepest overlap among the 2×2 body circles.
        const afx = dcos(ca.h), afy = dsin(ca.h);
        const bfx = dcos(cb.h), bfy = dsin(cb.h);
        let best = 0;
        let nx = 0;
        let ny = 0;
        for (const oa of [-BODY_OFFSET, BODY_OFFSET]) {
          for (const ob of [-BODY_OFFSET, BODY_OFFSET]) {
            const px = cb.x + bfx * ob - (ca.x + afx * oa);
            const py = cb.y + bfy * ob - (ca.y + afy * oa);
            const d = Math.sqrt(px * px + py * py);
            const pen = 2 * BODY_RADIUS - d;
            if (pen > best) {
              best = pen;
              if (d > 1e-9) {
                nx = px / d;
                ny = py / d;
              } else {
                nx = 1;
                ny = 0;
              }
            }
          }
        }
        if (best <= 0) continue;
        // Separate (only into free space: walls win).
        const half = best / 2;
        const ax = ca.x - nx * half, ay = ca.y - ny * half;
        const bx = cb.x + nx * half, by = cb.y + ny * half;
        if (this.track.isFree(ax, ay, 6)) { ca.x = ax; ca.y = ay; }
        if (this.track.isFree(bx, by, 6)) { cb.x = bx; cb.y = by; }
        ca.contact = true;
        cb.contact = true;
        // Velocities along the contact normal (n points from a to b).
        const va = ca.vx * nx + ca.vy * ny;
        const vb = cb.vx * nx + cb.vy * ny;
        const closing = va - vb;
        if (closing <= 0) continue;
        this.events.push({ type: 'contact', a: i, b: j, impact: closing });
        if (closing > CAR_CRASH_PXS) {
          // Fault: whoever is driving into the other. Head-on: both.
          const aInto = va;
          const bInto = -vb;
          const headOn = aInto > CAR_CRASH_PXS * 0.5 && bInto > CAR_CRASH_PXS * 0.5;
          if (headOn || aInto >= bInto) this.crashCar(a);
          if (headOn || bInto > aInto) this.crashCar(b);
          continue;
        }
        // Equal masses: exchange momentum along n with some bounce.
        const jn = ((1 + CAR_RESTITUTION) * closing) / 2;
        ca.vx -= jn * nx;
        ca.vy -= jn * ny;
        cb.vx += jn * nx;
        cb.vy += jn * ny;
      }
    }
  }

  private crashCar(r: Racer): void {
    if (r.car.crashed) return;
    r.car.crashed = true;
    r.car.crashTicks = 0;
    r.car.vx = 0;
    r.car.vy = 0;
    this.onCrash(r);
  }

  /** Race distance covered: laps × length + distance past the line. */
  progress(r: Racer): number {
    if (r.status === 'out') return r.outProgress;
    const L = this.track.length;
    let rel = this.track.progressAt(r.car.x, r.car.y) - this.track.startS;
    if (r.lap.startTick < 0) {
      // Still behind the line on the grid / formation.
      if (rel > 0) rel -= L;
      return rel;
    }
    rel = ((rel % L) + L) % L;
    // Just crossed the line but projection is still behind it.
    if (rel > L * 0.9 && this.tick - r.lap.startTick < 2 * TICK_HZ) rel -= L;
    return r.lap.lapsDone * L + rel;
  }

  /** Racer indices in race order. */
  standings(): number[] {
    const order = (r: Racer): [number, number] => {
      if (r.status === 'finished') return [0, this.finishOrder.indexOf(r.index)];
      if (r.status === 'running') return [1, -this.progress(r)];
      return [2, -r.outProgress];
    };
    return this.racers
      .map((r) => ({ i: r.index, k: order(r) }))
      .sort((p, q) => p.k[0] - q.k[0] || p.k[1] - q.k[1] || p.i - q.i)
      .map((p) => p.i);
  }

  results(): ResultRow[] {
    return this.standings().map((idx, pos) => {
      const r = this.racers[idx];
      return {
        racer: idx,
        position: pos + 1,
        status: r.status,
        laps: r.lap.lapsDone,
        timeTicks: r.status === 'finished' ? r.finishTick : null,
        bestLapTicks: r.lap.bestLapTicks,
        points: POINTS[pos] ?? 0,
      };
    });
  }

  /** Run to the end without rendering (after the human finishes or retires). */
  simulateToEnd(maxTicks = 3 * 60 * TICK_HZ): void {
    for (let i = 0; i < maxTicks && !this.over; i++) this.step();
  }

  hash(): number {
    const v: number[] = [this.tick];
    for (const r of this.racers) v.push(r.car.x, r.car.y, r.car.vx, r.car.vy, r.car.h, r.lap.lapsDone, r.status === 'running' ? 0 : r.status === 'finished' ? 1 : 2);
    return hashNumbers(v);
  }
}

/** Used to drive a human's car on the cool-down lap after the flag. */
const COOLDOWN_DRIVER: DriverSpec = {
  id: 'cooldown', name: '', tag: '', teamId: '', pace: 1, consistency: 1, aggression: 0, shortcut: 0, mistakeRate: 0, bio: '',
};

/**
 * Qualifying lap for an AI driver, simulated instantly: ticks, or null if
 * they crashed (back of the grid, like the original's 9:59:99).
 */
export function qualifyAi(track: Track, spec: RacerSpec, aiPace: number, seed: number): number | null {
  // One-lap qualifying is a lottery for the inconsistent: some days the lap
  // just doesn't come together (a scrappy lap costs pace and top speed).
  const rnd = mulberry32((seed ^ 0x51ed) >>> 0);
  const consistency = spec.driver?.consistency ?? 0.9;
  const scrappy = rnd() < 0.55 - consistency * 0.45;
  const jitter = 1 - rnd() * 0.06 * (1.6 - consistency) - (scrappy ? 0.05 : 0);
  const w = new RaceWorld(track, [spec], [0], { laps: 1, crashRule: 'classic', aiPace: aiPace * jitter, seed });
  if (scrappy) w.racers[0].perf = { ...w.racers[0].perf, vmax: w.racers[0].perf.vmax * 0.96 };
  for (let i = 0; i < 90 * TICK_HZ && !w.over; i++) {
    w.step();
    for (const e of w.events) if (e.type === 'lap') return e.valid ? e.ticks : null;
  }
  return null;
}

/** Grid order from qualifying times (null = no time = back). Stable by index. */
export function gridFromQualifying(times: (number | null)[]): number[] {
  return times
    .map((t, i) => ({ t: t ?? Infinity, i }))
    .sort((a, b) => a.t - b.t || a.i - b.i)
    .map((x) => x.i);
}
